import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useState } from 'react';
import {
  LAMBDA_MEMORY_MB_INPUT_PROPS,
  LAMBDA_TIMEOUT_SECONDS_INPUT_PROPS,
} from 'config-editor-common';
import type { ValkyrieLambdaDefaults } from 'config-editor-common';

const useStyles = makeStyles(theme => ({
  defaultsSection: {
    marginBottom: theme.spacing(3),
    '&:before': { display: 'none' },
  },
  defaultsSummary: {
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px dashed ${theme.palette.divider}`,
    minHeight: 48,
    '&.Mui-expanded': { minHeight: 48 },
  },
  defaultsDetails: {
    flexDirection: 'column',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderTop: `1px dashed ${theme.palette.divider}`,
  },
}));

interface ValkyrieDefaultsEditorProps {
  defaults?: ValkyrieLambdaDefaults;
  onChange: (defaults: ValkyrieLambdaDefaults) => void;
}

export const ValkyrieDefaultsEditor = ({
  defaults: propsDefaults,
  onChange,
}: ValkyrieDefaultsEditorProps) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  const defaults: ValkyrieLambdaDefaults = {
    lambda_source: propsDefaults?.lambda_source ?? '',
    build_number: propsDefaults?.build_number ?? 'latest',
    s3_key: propsDefaults?.s3_key ?? '',
    timeout: propsDefaults?.timeout ?? 24,
    memory_size: propsDefaults?.memory_size ?? 1024,
    env_vars: propsDefaults?.env_vars ?? {},
  };

  const update = (field: keyof ValkyrieLambdaDefaults, value: unknown) => {
    onChange({ ...defaults, [field]: value });
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      className={classes.defaultsSection}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.defaultsSummary}>
        <Typography variant="subtitle2">
          Endpoint Lambda Defaults
          {defaults.lambda_source ? ` (${defaults.lambda_source})` : ''}
        </Typography>
      </AccordionSummary>
      <AccordionDetails className={classes.defaultsDetails}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Lambda Source"
              value={defaults.lambda_source || ''}
              onChange={e => update('lambda_source', e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="e.g., valkyrie-dotnet"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Build Number"
              value={defaults.build_number || ''}
              onChange={e => update('build_number', e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="e.g., latest"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="S3 Key"
              value={defaults.s3_key || ''}
              onChange={e => update('s3_key', e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="e.g., valkyrie/lambdas/xxx.zip"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Timeout (s)"
              type="number"
              value={defaults.timeout ?? 24}
              onChange={e => update('timeout', parseInt(e.target.value, 10) || 24)}
              fullWidth
              variant="outlined"
              size="small"
              inputProps={{ ...LAMBDA_TIMEOUT_SECONDS_INPUT_PROPS }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Memory (MB)"
              type="number"
              value={defaults.memory_size ?? 1024}
              onChange={e => update('memory_size', parseInt(e.target.value, 10) || 1024)}
              fullWidth
              variant="outlined"
              size="small"
              inputProps={{ ...LAMBDA_MEMORY_MB_INPUT_PROPS }}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
